import { Component, OnInit } from '@angular/core';
import { Platform, RefresherCustomEvent } from '@ionic/angular';
import { filter, Observable, Subscription } from 'rxjs';
import { AnalyseDataService } from 'src/app/services/analyse/analyse-data.service';
import {
  AnalyzedConsumption,
  Hours,
  Prediction,
  SmartScore
} from 'src/app/services/analyse/analyse.types';
import { MeteorologiDataService } from 'src/app/services/meteorologi/meteorologi-data.service';
import {
  MeteorologyObservations,
  MeteorologyForecast
} from 'src/app/services/meteorologi/meteorologi.types';
import { AnalyticsService } from 'src/app/services/analytics/analytics.service';
import { AvtaleService } from 'src/app/services/avtale/avtale.service';
import { Avtaler } from 'src/app/services/avtale/avtale.types';
import { DataService } from 'src/app/services/datatransfer/DataService';
import { ForbrukService } from 'src/app/services/forbruk/forbruk.service';
import { Anlegg } from 'src/app/services/forbruk/forbruk.types';
import { InformationService } from 'src/app/services/notify/information.service';
import { InformationMessage } from 'src/app/services/notify/information.types';
import { Router, RouterEvent } from '@angular/router';

@Component({
  selector: 'app-analyse',
  templateUrl: './analyse.component.html',
  styleUrls: ['./analyse.component.scss']
})
export class AnalyseComponent implements OnInit {
  _showAnleggsliste: boolean = false;
  anleggList: Anlegg[] = [];
  valgtAnlegg: Anlegg;
  mineAvtaler: Avtaler[];
  valgtAvtale: Avtaler;
  netteier: string;
  hasNoConfig: boolean = false;
  loading: boolean = true;

  analyzedConsumption: AnalyzedConsumption;
  smartScore: SmartScore;
  prediction: Prediction;
  currentHighThreeDayMax: Hours[];
  errorAnalyse: boolean = false;
  errorScore: boolean = false;
  errorMeteorologyObservations: boolean = false;
  errorMeteorologyForecast: boolean = false;
  dataSubscription$: Subscription;
  meteorologyObservations: MeteorologyObservations;
  meteorologyForecast: MeteorologyForecast;

  meteorologyObservations$: Observable<MeteorologyObservations>;
  meteorologyForecast$: Observable<MeteorologyForecast>;
  meteorologyObservationsSubscription: Subscription;
  meteorologyForecastSubscription: Subscription;
  analyzeResult$: Observable<AnalyzedConsumption>;
  smartScoreResult$: Observable<SmartScore>;
  analyzeSubscription: Subscription;
  smartScoreSubscription: Subscription;
  avtaleSubscription: Subscription;
  forbrukSubscription: Subscription;
  infoMessages: InformationMessage[] = [];

  constructor(
    public platform: Platform,
    private _forbrukService: ForbrukService,
    private _avtaleService: AvtaleService,
    private _analyseDataService: AnalyseDataService,
    private _meteorologiDataService: MeteorologiDataService,
    private _analytics: AnalyticsService,
    private dataService: DataService,
    public infoService: InformationService,
    private router: Router
  ) {
    this.analyzeResult$ = this._analyseDataService.analyzedConsumption$;
    this.smartScoreResult$ = this._analyseDataService.smartScore$;
    this.meteorologyObservations$ =
      this._meteorologiDataService.meteorologyObservations$;
    this.meteorologyForecast$ =
      this._meteorologiDataService.meteorologyForecast$;

    router.events
      .pipe(
        filter(
          (e: any): e is RouterEvent =>
            e instanceof RouterEvent && e.url.includes('analyse')
        )
      )
      .subscribe((e: RouterEvent) => {
        this.chooseActiveNavAnlegg();
      });
  }

  ngOnInit() {
    this.infoService.getInfoMessages().subscribe({
      next: (res) => {
        this.infoMessages = res.filter((x) => x.aktivert) || [];
      }
    });
    this.fetchData();
    this._analytics.logEvent_selectContent('analysis_opened');
  }

  ionViewWillEnter() {
    this.dataSubscription$ = this.dataService
      .getKundebytte()
      .subscribe((new_kunde_id) => {
        const currentId = this.valgtAvtale?.kunde_id;
        if (new_kunde_id !== currentId) {
          this.loading = true;
          this.hasNoConfig = false;
          this.valgtAnlegg = undefined;
          this.smartScore = undefined;
          this.analyzedConsumption = undefined;
          this._analyseDataService.reset();
          this.analyzeResult$ = this._analyseDataService.analyzedConsumption$;
          this.smartScoreResult$ = this._analyseDataService.smartScore$;
          this.meteorologyObservations$ =
            this._meteorologiDataService.meteorologyObservations$;
          this.meteorologyForecast$ =
            this._meteorologiDataService.meteorologyForecast$;
          this.fetchData();
        }
      });
  }

  ionViewDidLeave() {
    this.dataSubscription$?.unsubscribe();
    this.avtaleSubscription?.unsubscribe();
    this.analyzeSubscription?.unsubscribe();
    this.smartScoreSubscription?.unsubscribe();
    this.forbrukSubscription?.unsubscribe();
    this.meteorologyObservationsSubscription?.unsubscribe();
    this.meteorologyForecastSubscription?.unsubscribe();
  }

  chooseActiveNavAnlegg() {
    const navState = this.router.lastSuccessfulNavigation?.extras?.state;
    const activeAnlegg = this.anleggList?.find(
      (x) => x.objectid === navState?.objectid
    );
    if (activeAnlegg) {
      this.setAnlegg(activeAnlegg);
    }
  }

  fetchData() {
    this.forbrukSubscription = this._forbrukService
      .getDagForbrukSiste(true)
      .subscribe({
        next: (res) => {
          this.anleggList = res.anlegg
            ?.filter((anlegg) => !this.anleggIsInactive(anlegg))
            .sort((a, b) => b.priser.forbrukKWh - a.priser.forbrukKWh);
          if (this.anleggList?.length > 0) {
            const navState =
              this.router.lastSuccessfulNavigation?.extras?.state;
            const activeAnlegg = this.anleggList?.find(
              (x) => x.objectid === navState?.objectid
            );
            if (activeAnlegg) {
              this.valgtAnlegg = activeAnlegg;
            }

            if (this.valgtAnlegg) {
              if (
                !this.anleggList.find(
                  (anlegg) => anlegg.objectid === this.valgtAnlegg.objectid
                )
              ) {
                this._analyseDataService.reset();
                this.analyzeResult$ =
                  this._analyseDataService.analyzedConsumption$;
                this.smartScoreResult$ = this._analyseDataService.smartScore$;
                this.valgtAnlegg = this.anleggList[0];
              } else {
                this.valgtAnlegg = this.anleggList.find(
                  (anlegg) => anlegg.objectid === this.valgtAnlegg.objectid
                );
              }
            } else {
              this.valgtAnlegg = this.anleggList[0];
            }

            // Vis dropdown med anlegg om vi har flere
            if (this.anleggList.length > 1) {
              this._showAnleggsliste = true;
            } else {
              this._showAnleggsliste = false;
            }
            this._analyseDataService.fetchData(this.valgtAnlegg.objectid);
            this.getAnalyzedConsumption();
            this.getSmartScore();
            this._meteorologiDataService.fetchData(this.valgtAnlegg.objectid);
            this.getMeteorology();
            this.getAvtaler();
          } else {
            this.valgtAnlegg = undefined;
            this._showAnleggsliste = false;
            this.loading = false;
          }
        }
      });
  }

  getAvtaler() {
    this.avtaleSubscription = this._avtaleService.getAvtaler().subscribe({
      next: (res) => {
        this.mineAvtaler = res.avtaler;
        this.valgtAvtale = this.mineAvtaler.find(
          (avtale) => avtale.objectid === this.valgtAnlegg.objectid
        );
      }
    });
  }

  anleggIsInactive(anlegg: Anlegg) {
    return anlegg.tildato ? new Date(anlegg.tildato) < new Date() : false;
  }

  getAnalyzedConsumption() {
    this.errorAnalyse = false;
    this.analyzeSubscription = this.analyzeResult$.subscribe({
      next: (res) => {
        if (!res || !res.nettleietrinn) {
          this.errorAnalyse = true;
        } else {
          if (res.nettleietrinn.result === 'NO_CONFIG') {
            this.hasNoConfig = true;
            this.netteier = res.nettleietrinn.netteier;
          } else {
            this.analyzedConsumption = res;
            this.netteier = res.nettleietrinn.netteier;
            this.currentHighThreeDayMax =
              this.analyzedConsumption.nettleietrinn.hours_high_three;
          }
        }
        this.loading = false;
      }
    });
  }

  getSmartScore() {
    this.errorScore = false;
    this.smartScoreSubscription = this.smartScoreResult$.subscribe({
      next: (res) => {
        if (res) {
          this.smartScore = res;
        } else {
          this.errorScore = true;
        }
        this.loading = false;
      }
    });
  }

  getMeteorology() {
    this.errorMeteorologyObservations = false;
    this.errorMeteorologyForecast = false;
    this.meteorologyObservationsSubscription =
      this.meteorologyObservations$.subscribe({
        next: (res) => {
          if (res) {
            this.meteorologyObservations = res;
          } else {
            this.errorMeteorologyObservations = true;
          }
        }
      });
    this.meteorologyForecastSubscription = this.meteorologyForecast$.subscribe({
      next: (res) => {
        if (res) {
          this.meteorologyForecast = res;
        } else {
          this.errorMeteorologyForecast = true;
        }
      }
    });
  }

  setAnlegg(anlegg: Anlegg) {
    this.loading = true;
    this.valgtAnlegg = anlegg;
    this.valgtAvtale = this.mineAvtaler?.find(
      (avtale) => avtale.objectid === anlegg.objectid
    );
    this._analyseDataService.reset();
    this.analyzeResult$ = this._analyseDataService.analyzedConsumption$;
    this.smartScoreResult$ = this._analyseDataService.smartScore$;
    this._analyseDataService.fetchData(this.valgtAnlegg.objectid);
    this.getAnalyzedConsumption();
    this.getSmartScore();
    this._meteorologiDataService.reset();
    this.meteorologyObservations$ =
      this._meteorologiDataService.meteorologyObservations$;
    this._meteorologiDataService.fetchData(this.valgtAnlegg.objectid);
    this.getMeteorology();
  }

  async doRefresh(myevent: RefresherCustomEvent) {
    this.loading = true;
    this.fetchData();
    myevent.detail.complete();
  }
}
